import type { UserEditableFormControlElement } from "../utils/types";

export function validateRequired(field: UserEditableFormControlElement): string {
    const value = field.value?.trim();
    if (!value) {
        return field.getAttribute("data-val-required") || "This field is required.";
    }

    return "";
};

export function validateEmail(field: UserEditableFormControlElement): string {
    const value = field.value?.trim();
    if (!value) return "";

    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!regex.test(value)) {
        return field.getAttribute("data-val-email") || "Please enter a valid email address.";
    }

    return "";
};

export function validateLength(field: UserEditableFormControlElement): string {
    const value = field.value.trim();
    if (!value) return "";

    const min = Number(field.getAttribute("data-val-length-min") || 0);
    const max = Number(field.getAttribute("data-val-length-max") || Infinity);

    const msg = field.getAttribute("data-val-length");
    const rangeMsg = `The value must be between ${min} and ${max} characters long.`;

    if (min > 0 && value.length < min) {
        if (msg) {
            return msg;
        }

        return (max < Infinity) ? rangeMsg : `The value must be at least ${min} characters long.`;
    }

    if (max < Infinity && value.length > max) {
        if (msg) {
            return msg;
        }

        return (min > 0) ? rangeMsg : `The value must be at most ${max} characters long.`;
    }

    return "";
}

export function validateMinLength(field: UserEditableFormControlElement): string {
    const value = field.value.trim();
    if (!value) return "";

    const min = Number(field.getAttribute("data-val-minlength-min") || 0);
    const max = Number(field.getAttribute("data-val-maxlength-max") || Infinity);

    const minMsg = field.getAttribute("data-val-minlength");
    const maxMsg = field.getAttribute("data-val-maxlength");
    const rangeMsg = `The value must be between ${min} and ${max} characters long.`;

    if (value.length < min) {
        if (minMsg) {
            return minMsg;
        }

        return (max < Infinity) ? rangeMsg : `The value must be at least ${min} characters long.`;
    }

    if (max < Infinity && value.length > max) {
        if (maxMsg) {
            return maxMsg;
        } 

        return (min > 0) ? rangeMsg : `The value must be at most ${max} characters long.`;
    }

    return "";
}

// note: validateMinLength already handles cases where both attributes exist.
export function validateMaxLength(field: UserEditableFormControlElement): string {
    const value = field.value.trim();
    if (!value) return "";

    const max = Number(field.getAttribute("data-val-maxlength-max") || Infinity);

    if (value.length > max) {
        return field.getAttribute("data-val-maxlength") || `The value must be at most ${max} characters long.`;
    }

    return "";
}

export function validateRegex(field: UserEditableFormControlElement): string {
    const value = field.value?.trim();
    if (!value) return "";

    const patternAttr = field.getAttribute('data-val-regex-pattern') || "";

    let pattern: RegExp;
    try {
        pattern = new RegExp(patternAttr);
    } catch {
        return ""; // invalid pattern -> ignore rule
    }


    if (!pattern.test(value)) {
        return field.getAttribute('data-val-regex') || "The value does not match the required pattern.";
    }

    return "";
};

export function validateRange(field: UserEditableFormControlElement): string {
    const rawValue = field.value?.trim();
    if (!rawValue) return "";

    const value = Number(preformatFloat(rawValue) ?? 0);

    if (Number.isNaN(value)) {
        return `Value must be a number.`;
    }

    const min = Number(field.getAttribute("data-val-range-min") || 0);
    const max = Number(field.getAttribute("data-val-range-max") || Infinity);

    if ((!Number.isNaN(min) && value < min) || (!Number.isNaN(max) && value > max)) {
        return field.getAttribute('data-val-range') || `The value must be between ${min} and ${max}.`;
    }

    return "";
};

export function validateCompare(field: UserEditableFormControlElement, form: HTMLFormElement): string {
    const selector = field.getAttribute('data-val-equalto-other') || "";
    // MVC uses something like "*.Password" so we remove "*."
    const targetName = selector.replace('*.', '');

    if (targetName) {
        const target = form.querySelector<UserEditableFormControlElement>(`[name="${targetName}"]`);
        if (target && target.value !== field?.value) {
            return field.getAttribute('data-val-equalto') || "The values do not match.";
        }
    }

    return "";
};


function preformatFloat(floatStr: string): string {
    if (!floatStr) return "";

    const commaPos = floatStr.indexOf(',');
    if (commaPos === -1) {
        return floatStr;
    };

    const dotPos = floatStr.indexOf('.');
    if (dotPos === -1) {
        return floatStr.replace(/\,/g, '.');
    };

    return commaPos < dotPos ?
        floatStr.replace(/\,/g, '') :
        floatStr.replace(/\./g, '').replace(',', '.');
};
